import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  MessagePayload,
  onMessage,
  Unsubscribe,
} from 'firebase/messaging';
import { BehaviorSubject, Observable, Subject, firstValueFrom, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ApiService } from '../../shared/services/api/api.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { getFirebaseConfig } from './firebase.config';
import { FirebaseService } from './firebase.service';
import { getOrCreateDeviceId } from './device-id.util';
import {
  buildFcmRegistrationFingerprint,
  clearFcmRegistrationMeta,
  delay,
  getStoredFcmRegistrationFingerprint,
  getStoredFcmRegistrationMeta,
  parseFcmRegistrationError,
  storeFcmRegistrationMeta,
} from './fcm-registration.util';
import {
  FcmRegistrationResult,
  FcmRegistrationState,
} from './models/fcm-registration-state.model';
import {
  SaveDeviceTokenParams,
  SaveDeviceTokenRequest,
  SaveDeviceTokenResponse,
} from './models/device-token.model';
import { FcmForegroundNotification } from './models/fcm-foreground-notification.model';

/** Browser notification permission state (Notification.permission). */
export type NotificationPermissionState = 'granted' | 'denied' | 'default';

/** Outcome of the FCM permission + token flow. */
export interface FcmTokenResult {
  success: boolean;
  token: string | null;
  permission: NotificationPermissionState;
  error?: string;
  tokenChanged?: boolean;
}

const SAVE_TOKEN_MAX_ATTEMPTS = 3;
const BROWSER_NOTIFICATION_AUTO_DISMISS_MS = 5000;
const DEFAULT_FOREGROUND_NOTIFICATION_TITLE = 'Notification';

/**
 * FCM: browser notification permission, token generation, backend registration,
 * token lifecycle management, and foreground push message handling.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly firebaseService = inject(FirebaseService);
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);

  private readonly saveTokenEndpoint = '/save-token';

  private messaging: Messaging | null = null;
  private fcmToken: string | null = null;
  private registrationInFlight = false;
  private lifecycleUserId: number | null = null;
  private lifecycleMonitoringActive = false;
  private foregroundListenerActive = false;
  private foregroundMessageUnsubscribe: Unsubscribe | null = null;
  private readonly foregroundNotifications$ = new Subject<FcmForegroundNotification>();
  private readonly visibilityHandler = (): void => {
    void this.handleVisibilityChange();
  };

  private readonly registrationState$ = new BehaviorSubject<FcmRegistrationState>(
    this.buildInitialRegistrationState(),
  );

  /** Registration lifecycle state for validation UI and diagnostics. */
  getRegistrationState(): Observable<FcmRegistrationState> {
    return this.registrationState$.asObservable();
  }

  /** Synchronous snapshot of registration lifecycle state. */
  getCurrentRegistrationState(): FcmRegistrationState {
    return this.registrationState$.value;
  }

  /** Stream of foreground FCM messages received while the app tab is active. */
  getForegroundNotifications(): Observable<FcmForegroundNotification> {
    return this.foregroundNotifications$.asObservable();
  }

  /**
   * Subscribes to Firebase {@link onMessage} for foreground push handling.
   * Idempotent — safe to call multiple times (e.g. app init and post-login).
   */
  async startForegroundMessageListener(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.foregroundListenerActive) {
      return;
    }

    try {
      const messaging = await this.ensureMessaging();
      if (!messaging) {
        console.info('[FCM] Foreground listener not started — messaging is unavailable in this environment.');
        return;
      }

      this.foregroundMessageUnsubscribe = onMessage(messaging, (payload) => {
        this.handleForegroundMessage(payload);
      });
      this.foregroundListenerActive = true;
      console.info('[FCM] Foreground message listener started.');
    } catch (error) {
      const message = this.formatError(error);
      console.error('[FCM] Failed to start foreground message listener:', message, error);
    }
  }

  /**
   * Requests notification permission, then generates an FCM token when granted.
   * Safe to call once after {@link FirebaseService.initialize}.
   */
  async requestPermissionAndGenerateToken(): Promise<FcmTokenResult> {
    if (!isPlatformBrowser(this.platformId)) {
      return this.failureResult('denied', 'FCM is not available during server rendering.');
    }

    const supported = await isSupported();
    if (!supported) {
      return this.failureResult('denied', 'Firebase Cloud Messaging is not supported in this browser.');
    }

    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      return {
        success: false,
        token: null,
        permission,
        error: this.permissionErrorMessage(permission),
      };
    }

    return this.generateToken(permission);
  }

  /** In-memory FCM token from the last successful generation (null until then). */
  getStoredToken(): string | null {
    return this.fcmToken;
  }

  /** Stable device identifier for this browser (persisted in localStorage). */
  getDeviceId(): string {
    return getOrCreateDeviceId(this.platformId);
  }

  /**
   * After successful login: generate an FCM token when possible and register it with the backend.
   * Starts token lifecycle monitoring for change detection.
   */
  async registerTokenAfterLogin(userId: number): Promise<FcmRegistrationResult> {
    if (!isPlatformBrowser(this.platformId)) {
      return { success: false, error: 'FCM is not available during server rendering.' };
    }

    if (!this.authService.isAuthenticated()) {
      console.info('[FCM] Skipping post-login token registration — user is not authenticated.');
      return { success: false, error: 'User is not authenticated.' };
    }

    this.startTokenLifecycleMonitoring(userId);
    await this.startForegroundMessageListener();

    let fcmToken = this.fcmToken;
    if (!fcmToken) {
      console.info('[FCM] No in-memory token — attempting generation after login.');
      const result = await this.requestPermissionAndGenerateToken();
      if (!result.success || !result.token) {
        console.info(
          '[FCM] Skipping post-login token registration — token generation failed:',
          result.error ?? 'unknown error',
        );
        this.patchRegistrationState({
          status: 'failed',
          tokenLength: null,
          lastError: result.error ?? 'Token generation failed.',
          lastErrorCategory: 'client',
        });
        return { success: false, error: result.error ?? 'Token generation failed.' };
      }
      fcmToken = result.token;
    }

    return this.registerTokenForUser(userId, { fcmToken });
  }

  /**
   * Re-fetches the FCM token silently (no permission prompt) and re-registers when it changed.
   */
  async detectAndRegisterTokenChange(userId?: number): Promise<FcmRegistrationResult> {
    const resolvedUserId = userId ?? this.lifecycleUserId;
    if (!resolvedUserId) {
      return { success: false, error: 'User id is required for token change registration.' };
    }

    if (!this.authService.isAuthenticated()) {
      console.info('[FCM] Skipping token change registration — user is not authenticated.');
      return { success: false, error: 'User is not authenticated.' };
    }

    const refreshResult = await this.refreshStoredTokenSilently();
    if (!refreshResult.success || !refreshResult.token) {
      return {
        success: false,
        error: refreshResult.error ?? 'Unable to refresh FCM token.',
        tokenChanged: false,
      };
    }

    if (!refreshResult.tokenChanged) {
      console.info('[FCM] Token unchanged — no re-registration needed.');
      this.patchRegistrationState({ tokenChanged: false });
      return {
        success: true,
        message: 'Token unchanged.',
        tokenChanged: false,
      };
    }

    console.info('[FCM] Token change detected — re-registering with backend.');
    this.patchRegistrationState({ tokenChanged: true });
    return this.registerTokenForUser(resolvedUserId, {
      fcmToken: refreshResult.token,
      force: true,
      tokenChanged: true,
    });
  }

  /**
   * Registers the current (or provided) FCM token for a user with retry on transient failures.
   */
  async registerTokenForUser(
    userId: number,
    options: {
      fcmToken?: string;
      force?: boolean;
      tokenChanged?: boolean;
    } = {},
  ): Promise<FcmRegistrationResult> {
    if (!isPlatformBrowser(this.platformId)) {
      return { success: false, error: 'FCM is not available during server rendering.' };
    }

    if (!this.authService.isAuthenticated()) {
      console.info('[FCM] Skipping token registration — user is not authenticated.');
      return { success: false, error: 'User is not authenticated.' };
    }

    const fcmToken = options.fcmToken ?? this.fcmToken;
    if (!fcmToken) {
      const message = 'No FCM token available. Generate a token first.';
      this.patchRegistrationState({
        status: 'failed',
        tokenLength: null,
        lastError: message,
        lastErrorCategory: 'client',
      });
      return { success: false, error: message };
    }

    if (this.registrationInFlight) {
      console.info('[FCM] Skipping token registration — registration already in progress.');
      return { success: false, error: 'Registration already in progress.' };
    }

    const deviceId = this.getDeviceId();
    const fingerprint = buildFcmRegistrationFingerprint(userId, deviceId, fcmToken);

    if (!options.force && this.isTokenAlreadyRegistered(fingerprint)) {
      const meta = getStoredFcmRegistrationMeta(this.platformId);
      console.info(
        '[FCM] Skipping token registration — already registered for this user, device, and token.',
      );
      this.patchRegistrationState({
        status: 'registered',
        tokenLength: fcmToken.length,
        lastRegisteredAt: meta?.registeredAt || this.registrationState$.value.lastRegisteredAt,
        lastError: null,
        lastErrorCategory: null,
        retryCount: 0,
        tokenChanged: options.tokenChanged ?? false,
      });
      return {
        success: true,
        message: 'Token already registered.',
        tokenChanged: options.tokenChanged ?? false,
      };
    }

    this.registrationInFlight = true;
    this.patchRegistrationState({
      status: 'pending',
      tokenLength: fcmToken.length,
      lastError: null,
      lastErrorCategory: null,
      tokenChanged: options.tokenChanged ?? false,
    });

    try {
      const response = await this.saveTokenWithRetry({ userId, deviceId, fcmToken });
      const registeredAt = new Date().toISOString();

      storeFcmRegistrationMeta(this.platformId, {
        fingerprint,
        registeredAt,
        userId,
      });

      this.patchRegistrationState({
        status: 'registered',
        tokenLength: fcmToken.length,
        lastRegisteredAt: registeredAt,
        lastError: null,
        lastErrorCategory: null,
        retryCount: response.retryCount,
        tokenChanged: options.tokenChanged ?? false,
      });

      console.info('[FCM] Token registration completed.', {
        userId,
        tokenChanged: options.tokenChanged ?? false,
        retryCount: response.retryCount,
      });

      return {
        success: true,
        message: response.message,
        tokenChanged: options.tokenChanged ?? false,
        retryCount: response.retryCount,
      };
    } catch (error) {
      const parsed = parseFcmRegistrationError(error);
      console.error('[FCM] Token registration failed:', parsed);

      this.patchRegistrationState({
        status: 'failed',
        tokenLength: fcmToken.length,
        lastError: parsed.message,
        lastErrorCategory: parsed.category,
      });

      return {
        success: false,
        error: parsed.message,
        errorCategory: parsed.category,
        tokenChanged: options.tokenChanged ?? false,
      };
    } finally {
      this.registrationInFlight = false;
    }
  }

  /** Clears local registration metadata and stops lifecycle monitoring (e.g. on logout). */
  clearRegistrationState(): void {
    clearFcmRegistrationMeta(this.platformId);
    this.registrationInFlight = false;
    this.stopTokenLifecycleMonitoring();
    this.stopForegroundMessageListener();
    this.patchRegistrationState(this.buildInitialRegistrationState());
  }

  /** Starts listening for tab visibility to detect FCM token rotation. */
  startTokenLifecycleMonitoring(userId: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.lifecycleUserId = userId;

    if (this.lifecycleMonitoringActive) {
      return;
    }

    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.lifecycleMonitoringActive = true;
    console.info('[FCM] Token lifecycle monitoring started for user', userId);
  }

  /** Stops token lifecycle monitoring. */
  stopTokenLifecycleMonitoring(): void {
    if (!isPlatformBrowser(this.platformId) || !this.lifecycleMonitoringActive) {
      this.lifecycleUserId = null;
      return;
    }

    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.lifecycleMonitoringActive = false;
    this.lifecycleUserId = null;
    console.info('[FCM] Token lifecycle monitoring stopped.');
  }

  /**
   * Registers or updates an FCM device token for the given user and device.
   * Prefer {@link registerTokenForUser} for retry, state tracking, and deduplication.
   */
  saveToken(params: SaveDeviceTokenParams): Observable<SaveDeviceTokenResponse> {
    const request = this.buildSaveTokenRequest(params);

    console.info('[FCM] Registering device token with backend…', {
      user_id: request.user_id,
      device_id: request.device_id,
      fcm_token_length: request.fcm_token.length,
    });

    return this.apiService
      .post<SaveDeviceTokenResponse>(this.saveTokenEndpoint, request, false, true)
      .pipe(
        tap((response) => {
          console.info('[FCM] Device token registered successfully:', response.message);
        }),
        catchError((error: unknown) => {
          const parsed = parseFcmRegistrationError(error);
          console.error('[FCM] Device token registration failed:', parsed);
          return throwError(() => parsed);
        }),
      );
  }

  /** Current browser notification permission without prompting the user. */
  getPermissionStatus(): NotificationPermissionState {
    if (!isPlatformBrowser(this.platformId) || !('Notification' in window)) {
      return 'denied';
    }

    return Notification.permission;
  }

  /**
   * Prompts for notification permission when not already decided.
   * Returns the resulting permission state without generating a token.
   */
  async requestPermission(): Promise<NotificationPermissionState> {
    if (!isPlatformBrowser(this.platformId)) {
      return 'denied';
    }

    if (!('Notification' in window)) {
      console.warn('[FCM] Notifications API is not available in this browser.');
      return 'denied';
    }

    const current = Notification.permission;

    if (current === 'granted') {
      console.info('[FCM] Notification permission already granted.');
      return 'granted';
    }

    if (current === 'denied') {
      console.warn(
        '[FCM] Notification permission denied. Enable notifications in browser site settings to receive push messages.',
      );
      return 'denied';
    }

    const result = await Notification.requestPermission();

    switch (result) {
      case 'granted':
        console.info('[FCM] Notification permission granted.');
        return 'granted';
      case 'denied':
        console.warn('[FCM] Notification permission denied by the user.');
        return 'denied';
      default:
        console.info('[FCM] Notification permission dismissed (still default).');
        return 'default';
    }
  }

  /**
   * Silently refreshes the in-memory token when permission is already granted.
   * Detects rotation by comparing with the previous in-memory value.
   */
  async refreshStoredTokenSilently(): Promise<{
    success: boolean;
    token: string | null;
    tokenChanged: boolean;
    error?: string;
  }> {
    if (!isPlatformBrowser(this.platformId)) {
      return { success: false, token: null, tokenChanged: false, error: 'Not in browser.' };
    }

    if (this.getPermissionStatus() !== 'granted') {
      return {
        success: false,
        token: null,
        tokenChanged: false,
        error: 'Notification permission is not granted.',
      };
    }

    const supported = await isSupported();
    if (!supported) {
      return {
        success: false,
        token: null,
        tokenChanged: false,
        error: 'Firebase Cloud Messaging is not supported in this browser.',
      };
    }

    const previousToken = this.fcmToken;
    const result = await this.generateToken('granted');

    if (!result.success || !result.token) {
      return {
        success: false,
        token: null,
        tokenChanged: false,
        error: result.error ?? 'Token refresh failed.',
      };
    }

    const tokenChanged = !!previousToken && previousToken !== result.token;
    if (tokenChanged) {
      console.info('[FCM] Token rotation detected.', {
        previousLength: previousToken?.length ?? 0,
        newLength: result.token.length,
      });
    }

    return { success: true, token: result.token, tokenChanged };
  }

  private async saveTokenWithRetry(
    params: SaveDeviceTokenParams,
  ): Promise<{ message: string; retryCount: number }> {
    let retryCount = 0;

    for (let attempt = 1; attempt <= SAVE_TOKEN_MAX_ATTEMPTS; attempt++) {
      try {
        const response = await firstValueFrom(this.saveToken(params));
        return { message: response.message, retryCount };
      } catch (error) {
        const parsed = parseFcmRegistrationError(error);
        const canRetry = parsed.retryable && attempt < SAVE_TOKEN_MAX_ATTEMPTS;

        if (!canRetry) {
          throw parsed;
        }

        retryCount = attempt;
        const delayMs = 1000 * Math.pow(2, attempt - 1);
        console.warn(
          `[FCM] Registration attempt ${attempt}/${SAVE_TOKEN_MAX_ATTEMPTS} failed (${parsed.category}). Retrying in ${delayMs}ms…`,
          parsed.message,
        );
        await delay(delayMs);
      }
    }

    throw parseFcmRegistrationError(new Error('Token registration failed after retries.'));
  }

  private async handleVisibilityChange(): Promise<void> {
    if (document.visibilityState !== 'visible') {
      return;
    }

    if (!this.authService.isAuthenticated() || !this.lifecycleUserId) {
      return;
    }

    await this.detectAndRegisterTokenChange(this.lifecycleUserId);
  }

  private buildInitialRegistrationState(): FcmRegistrationState {
    const meta = getStoredFcmRegistrationMeta(this.platformId);
    const storedToken = this.fcmToken;

    return {
      status: meta ? 'registered' : 'idle',
      tokenLength: storedToken?.length ?? null,
      lastRegisteredAt: meta?.registeredAt || null,
      lastError: null,
      lastErrorCategory: null,
      retryCount: 0,
      tokenChanged: false,
    };
  }

  private patchRegistrationState(partial: Partial<FcmRegistrationState>): void {
    this.registrationState$.next({
      ...this.registrationState$.value,
      ...partial,
    });
  }

  private async generateToken(permission: NotificationPermissionState): Promise<FcmTokenResult> {
    const app = this.firebaseService.getApp();
    if (!app) {
      const message = 'Firebase app is not initialized. Call FirebaseService.initialize() first.';
      console.error(`[FCM] ${message}`);
      return { success: false, token: null, permission, error: message };
    }

    const { vapidKey } = getFirebaseConfig();
    if (!vapidKey) {
      const message =
        'VAPID key is missing. Add environment.firebase.vapidKey from Firebase Console → Cloud Messaging → Web Push certificates.';
      console.error(`[FCM] ${message}`);
      return { success: false, token: null, permission, error: message };
    }

    try {
      const messaging = await this.ensureMessaging();
      if (!messaging) {
        const message = 'Firebase Cloud Messaging is not supported in this browser.';
        console.error(`[FCM] ${message}`);
        return { success: false, token: null, permission, error: message };
      }

      void this.startForegroundMessageListener();

      const registration = await this.registerServiceWorker();
      const previousToken = this.fcmToken;

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!token) {
        const message = 'getToken() returned empty — check service worker and VAPID key configuration.';
        console.warn(`[FCM] ${message}`);
        return { success: false, token: null, permission, error: message };
      }

      const tokenChanged = !!previousToken && previousToken !== token;
      this.fcmToken = token;

      this.patchRegistrationState({ tokenLength: token.length, tokenChanged });

      console.info('[FCM] Token generated successfully (stored in memory):');
      console.log('[FCM] Token:', token);

      return { success: true, token, permission, tokenChanged };
    } catch (error) {
      const message = this.formatError(error);
      console.error('[FCM] Token generation failed:', message, error);
      return { success: false, token: null, permission, error: message };
    }
  }

  private stopForegroundMessageListener(): void {
    if (!this.foregroundListenerActive && !this.foregroundMessageUnsubscribe) {
      return;
    }

    if (this.foregroundMessageUnsubscribe) {
      this.foregroundMessageUnsubscribe();
      this.foregroundMessageUnsubscribe = null;
    }

    this.foregroundListenerActive = false;
    console.info('[FCM] Foreground message listener stopped.');
  }

  private handleForegroundMessage(payload: MessagePayload): void {
    try {
      console.info('[FCM] Foreground message received.');
      console.log('[FCM] Foreground payload:', payload);

      const notification = this.mapForegroundNotification(payload);
      this.foregroundNotifications$.next(notification);
      // Prevent duplicates when app is not visible
      if (document.visibilityState !== 'visible') {
        return;
      }
      this.showTemporaryBrowserNotification(notification.title, notification.body);
    } catch (error) {
      const message = this.formatError(error);
      console.error('[FCM] Failed to handle foreground message:', message, error);
    }
  }

  private mapForegroundNotification(payload: MessagePayload): FcmForegroundNotification {
    const title =
      payload.notification?.title?.trim() ||
      payload.data?.['title']?.trim() ||
      DEFAULT_FOREGROUND_NOTIFICATION_TITLE;
    const body =
      payload.notification?.body?.trim() || payload.data?.['body']?.trim() || '';

    return {
      messageId: payload.messageId ?? null,
      title,
      body,
      data: { ...payload.data },
      receivedAt: new Date().toISOString(),
      raw: payload,
    };
  }

  private showTemporaryBrowserNotification(title: string, body: string): void {
    if (!isPlatformBrowser(this.platformId) || !('Notification' in window)) {
      console.warn('[FCM] Cannot show browser notification — Notifications API is unavailable.');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.info('[FCM] Skipping browser notification — permission is not granted.');
      return;
    }

    try {
      const browserNotification = new Notification(title, {
        body: body || undefined,
        icon: '/assets/notifications.svg',
        tag: `fcm-foreground-${Date.now()}`,
      });

      browserNotification.onerror = (event) => {
        console.error('[FCM] Browser notification error:', event);
      };

      window.setTimeout(() => {
        browserNotification.close();
      }, BROWSER_NOTIFICATION_AUTO_DISMISS_MS);
    } catch (error) {
      const message = this.formatError(error);
      console.error('[FCM] Failed to display browser notification:', message, error);
    }
  }

  private async ensureMessaging(): Promise<Messaging | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const supported = await isSupported();
    if (!supported) {
      return null;
    }

    const app = this.firebaseService.getApp();
    if (!app) {
      return null;
    }

    if (!this.messaging) {
      this.messaging = getMessaging(app);
    }

    return this.messaging;
  }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });

    await navigator.serviceWorker.ready;
    return registration;
  }

  private failureResult(
    permission: NotificationPermissionState,
    error: string,
  ): FcmTokenResult {
    console.warn(`[FCM] ${error}`);
    return { success: false, token: null, permission, error };
  }

  private permissionErrorMessage(permission: NotificationPermissionState): string {
    switch (permission) {
      case 'denied':
        return 'Notification permission was denied.';
      case 'default':
        return 'Notification permission was dismissed or not granted.';
      default:
        return 'Notification permission was not granted.';
    }
  }

  private formatError(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  }

  private isTokenAlreadyRegistered(fingerprint: string): boolean {
    return getStoredFcmRegistrationFingerprint(this.platformId) === fingerprint;
  }

  private buildSaveTokenRequest(params: SaveDeviceTokenParams): SaveDeviceTokenRequest {
    const userId = String(params.userId).trim();
    const deviceId = params.deviceId.trim();
    const fcmToken = params.fcmToken.trim();

    if (!userId || !/^\d+$/.test(userId) || Number(userId) < 1) {
      throw new Error('userId must be a positive numeric string.');
    }

    if (!deviceId) {
      throw new Error('deviceId is required.');
    }

    if (!fcmToken) {
      throw new Error('fcmToken is required.');
    }

    return {
      user_id: userId,
      device_id: deviceId,
      fcm_token: fcmToken,
    };
  }
}
